import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ChevronLeft, ChevronRight, MoreHorizontal, Plus, RefreshCw, Search } from 'lucide-react'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { TooltipProvider } from '@/components/ui/tooltip'
import { FacturaBadge, FacturaMenuItems } from '@/components/facturas/FacturaDeCobro'
import { NuevaFacturaDialog } from '@/components/facturas/NuevaFacturaDialog'
import { useAccionesDeFactura } from '@/components/facturas/useAccionesDeFactura'
import { aPedidoManual } from '@/components/facturas/forms'
import { useBuscarFactura, useEmisorFacturacion, useFacturas, usePuedeFacturar } from '@/hooks/useFacturas'
import { usePermissions } from '@/hooks/usePermissions'
import { formatCurrency, formatDateOrDash } from '@/lib/utils'
import { FACTURAS_POR_PAGINA, type EstadoFactura, type FiltrosFacturas } from '@/services/facturasService'

const ESTADOS: { value: EstadoFactura; label: string }[] = [
  { value: 'autorizada', label: 'Autorizadas' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'enviada', label: 'En ARCA' },
  { value: 'rechazada', label: 'Rechazadas' },
  { value: 'revisar', label: 'A revisar' },
]

const DOC: Record<number, string> = { 80: 'CUIT', 96: 'DNI' }

/** Espera a que se deje de tipear para no consultar con cada tecla. */
function useValorDiferido<T>(valor: T, ms = 300): T {
  const [diferido, setDiferido] = useState(valor)
  useEffect(() => {
    const t = setTimeout(() => setDiferido(valor), ms)
    return () => clearTimeout(t)
  }, [valor, ms])
  return diferido
}

/** Facturas y notas de crédito emitidas por el estudio (ORR-255 fase 3). */
export function Facturas() {
  const { puede } = usePermissions()
  const puedeFacturar = usePuedeFacturar()
  const { data: emisor, isLoading: cargandoEmisor } = useEmisorFacturacion()
  const [busqueda, setBusqueda] = useState('')
  const [estado, setEstado] = useState<'todos' | EstadoFactura>('todos')
  const [clase, setClase] = useState<'todas' | 'factura' | 'nc'>('todas')
  const [pagina, setPagina] = useState(0)
  const [nuevaAbierta, setNuevaAbierta] = useState(false)
  const buscarFactura = useBuscarFactura()
  const busquedaDiferida = useValorDiferido(busqueda)

  const filtros = useMemo<FiltrosFacturas>(
    () => ({
      estado: estado === 'todos' ? undefined : estado,
      clase: clase === 'todas' ? undefined : clase,
      busqueda: busquedaDiferida || undefined,
      pagina,
    }),
    [estado, clase, busquedaDiferida, pagina],
  )
  const { data, isLoading, isFetching, error, refetch } = useFacturas(filtros)
  const { onAccion, dialogos, accionFactura } = useAccionesDeFactura()
  const filas = data?.filas ?? []
  const total = data?.total ?? 0
  const paginas = Math.max(1, Math.ceil(total / FACTURAS_POR_PAGINA))

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        <PageHeader title="Facturas" description="Facturas electrónicas y notas de crédito emitidas" />

        <main className="flex-1 min-h-0 p-6 flex flex-col gap-6">
          {!cargandoEmisor && !emisor?.activo && (
            <div className="shrink-0 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              El estudio todavía no tiene un emisor de facturación activo. Hasta que se cargue, no se pueden emitir
              facturas.{' '}
              {puede.verConfiguracion && (
                <Link to="/configuracion/facturacion" className="underline font-medium">
                  Configurar el emisor
                </Link>
              )}
            </div>
          )}

          <div className="shrink-0 flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex flex-1 gap-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por receptor, número o documento..."
                  value={busqueda}
                  onChange={(e) => {
                    setBusqueda(e.target.value)
                    setPagina(0)
                  }}
                  className="pl-9"
                />
              </div>
              <Select value={estado} onValueChange={(v) => {
                  setEstado(v as typeof estado)
                  setPagina(0)
                }}>
                <SelectTrigger className="w-[160px]" aria-label="Estado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los estados</SelectItem>
                  {ESTADOS.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={clase} onValueChange={(v) => {
                  setClase(v as typeof clase)
                  setPagina(0)
                }}>
                <SelectTrigger className="w-[180px]" aria-label="Comprobante">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Facturas y notas</SelectItem>
                  <SelectItem value="factura">Solo facturas</SelectItem>
                  <SelectItem value="nc">Solo notas de crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" aria-label="Actualizar" onClick={() => refetch()}>
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {puedeFacturar && (
                <Button onClick={() => setNuevaAbierta(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva factura
                </Button>
              )}
            </div>
          </div>

          {error ? (
            <div className="shrink-0 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <p className="text-destructive">No se pudieron cargar las facturas.</p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 flex flex-col gap-3">
              <div className="flex-1 min-h-0 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Comprobante</TableHead>
                      <TableHead>Receptor</TableHead>
                      <TableHead className="text-right">Importe</TableHead>
                      <TableHead className="w-[50px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          Cargando facturas...
                        </TableCell>
                      </TableRow>
                    ) : filas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No hay facturas con estos filtros.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filas.map((f) => (
                        <TableRow key={f.id}>
                          <TableCell className="whitespace-nowrap">{formatDateOrDash(f.fecha ?? f.created_at)}</TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 items-start">
                              <FacturaBadge factura={f} />
                              {f.clase === 'nc' && !f.numero && <span className="text-xs text-muted-foreground">Nota de crédito</span>}
                              {f.clase === 'nc' && f.nc_total === false && <span className="text-xs text-muted-foreground">Parcial</span>}
                              {f.estado === 'rechazada' && f.error_mensaje && (
                                <span className="text-xs text-destructive max-w-xs">{f.error_mensaje}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span>{f.receptor_nombre || 'Consumidor final'}</span>
                              {DOC[f.receptor_doc_tipo] && (
                                <span className="text-xs text-muted-foreground">
                                  {DOC[f.receptor_doc_tipo]} {f.receptor_doc_nro}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {f.clase === 'nc' ? '−' : ''}
                            {formatCurrency(Number(f.importe_total))}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Acciones">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <FacturaMenuItems
                                  factura={f}
                                  puedeFacturar={puedeFacturar}
                                  puedeAnular={puede.anularFactura}
                                  puedeEliminarCobro={puede.eliminarMovimiento}
                                  onAccion={onAccion}
                                />
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {total > FACTURAS_POR_PAGINA && (
                <div className="shrink-0 flex items-center justify-end gap-2 text-sm">
                  <span className="text-muted-foreground">
                    Página {pagina + 1} de {paginas} · {total} comprobantes
                  </span>
                  <Button variant="outline" size="icon" aria-label="Página anterior" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Página siguiente"
                    disabled={pagina + 1 >= paginas}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </main>

        <NuevaFacturaDialog
          open={nuevaAbierta}
          onOpenChange={setNuevaAbierta}
          cuitEmisor={emisor?.cuit}
          buscarFactura={buscarFactura}
          onEmitir={async (values, solicitudId) => {
            const { receptor, lineas, enviarEmail } = aPedidoManual(values)
            await accionFactura.mutateAsync({ tipo: 'emitirManual', receptor, lineas, enviarEmail, solicitudId })
            toast.success('Factura pedida: se emite en unos segundos')
          }}
        />
        {dialogos}
      </div>
    </TooltipProvider>
  )
}
